/**
 * API functions for survey management
 */

import { SURVEY_ID_OFFSET } from '../constants/surveyConstants';
import { ASYNC_ANALYSIS_CONFIG, ANALYSIS_TASK_STATES } from '../config/constants';
import { SurveyValidationError } from './surveyValidation';
import type { ClientSignals } from './botDetection';

export {
  validateSurvey,
  SurveyValidationError,
  reportIsClean,
  hasBlockingErrors,
  autoFixableFixes,
  requiresTypedConfirmation,
} from './surveyValidation';
export type {
  ValidationIssue,
  ValidationReport,
  ValidateResult,
  IssueSeverity,
} from './surveyValidation';

/**
 * Convert survey ID to share ID (Base64 encoded with offset and randomization)
 */
export function surveyIdToShareId(surveyId: number): string {
  // Multiply survey ID by a large prime to spread out the values more
  const multipliedId = surveyId * 982451653; // Large prime number
  const offsetId = multipliedId + SURVEY_ID_OFFSET;
  const encoded = btoa(offsetId.toString()).replace(/=/g, '');
  return encoded;
}

/**
 * Convert share ID back to survey ID
 */
export function shareIdToSurveyId(shareId: string): number {
  // Decode + validate so a malformed/foreign share link fails cleanly (this is the
  // respondent entry point) instead of throwing a raw DOMException from atob() or
  // returning a bogus non-integer id that then gets sent to the API.
  let decoded: string;
  try {
    // Add padding back if needed
    const padding = 4 - (shareId.length % 4);
    if (padding !== 4) {
      shareId += '='.repeat(padding);
    }
    decoded = atob(shareId);
  } catch {
    throw new Error('Invalid survey link');
  }

  const offsetId = parseInt(decoded, 10);
  // Reverse the offset + multiplication used in encoding.
  const surveyId = (offsetId - SURVEY_ID_OFFSET) / 982451653; // Same prime as in encoding
  if (!Number.isInteger(surveyId) || surveyId <= 0) {
    throw new Error('Invalid survey link');
  }
  return surveyId;
}

/** A single thing a great answer should cover, with its probing priority. */
export interface RubricPoint {
  text: string;
  /** 0 = core (must cover), 1 = functional detail, 2 = value/why. */
  tier: number;
}

/**
 * Per-question probing rubric. Optional at authoring time — when absent, the backend
 * auto-derives one on save (see derive_survey_rubrics) and the author can edit it afterward.
 * Drives rubric-based adaptive follow-up probing when the survey's adaptive_probing flag is on.
 */
export interface QuestionRubric {
  information_goal?: string;
  /** Deepest tier the question genuinely wants: 0 = core, 1 = functional, 2 = value. */
  intended_depth?: number;
  points?: RubricPoint[];
}

export interface ParsedQuestion {
  id: string;
  text: string;
  type: string;
  choices?: string[];
  allow_other?: boolean;
  scale_min?: number;
  scale_max?: number;
  ranking_count?: number;
  followup_instructions?: string;
  enable_ai_followup?: boolean;
  rubric?: QuestionRubric;
  /** Force text-only input for this open-ended question (hides voice). Never set together with disable_text_input. */
  disable_voice_input?: boolean;
  /** Force voice-only input for this open-ended question (hides text). Never set together with disable_voice_input. */
  disable_text_input?: boolean;
  matrix_rows?: string[];
  matrix_columns?: string[];
  conjoint_attributes?: {name: string; levels: string[]}[];
  media_urls?: string[];
  description?: string;
}

/** Survey-level block metadata (question grouping / randomization). */
export interface SurveyBlockDefinition {
  id: string;
  label: string;
  randomize_within: boolean;
  display_condition?: {
    logic?: 'and' | 'or';
    conditions?: Array<{
      source_question_id: string;
      operator: 'selected' | 'not_selected' | '>' | '>=' | '=' | '<' | '<=';
      value: string;
      option_key?: string;
      threshold?: number;
    }>;
  };
}

export interface Survey {
  id: number;
  name: string;
  title?: string;
  description?: string;
  instructions?: string;
  state: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  questions?: ParsedQuestion[];
  blocks?: SurveyBlockDefinition[] | null;
  sessions_count?: number;
  responses_count?: number;
  redirect_config?: {
    redirect_base_url: string;
    tracking_param: { [key: string]: string };
    completion_params: { [key: string]: string } | null;
    screen_out_params: { [key: string]: string } | null;
    quota_full_params: { [key: string]: string } | null;
  };
  // Per-survey AI feature config (e.g. { redundancy_skip_enabled: true })
  ai_features?: {
    redundancy_skip_enabled?: boolean;
    redundancy_confidence_threshold?: number;
    // v3 breadth-first redundancy controls.
    // 'off' (default): never skip a whole question — every column is asked; only follow-up
    // suppression trims redundancy. 'carry_forward': skip the ask for follow-up-free covered
    // questions but FILL the cell with the respondent's own covering words. 'drop': legacy.
    redundancy_question_mode?: RedundancyQuestionMode;
    followup_suppression_enabled?: boolean;
    // Per-survey toggle for rubric-driven adaptive follow-up probing. Default off.
    adaptive_probing?: boolean;
    // Experimental conversational / discussion-guide style.
    survey_style?: SurveyStyle;
    discussion_guide?: DiscussionGuide;
  } | null;
  // Flattened flag exposed on the public survey payload for the respondent UI.
  ai_redundancy_skip_enabled?: boolean;
  redundancy_question_mode?: RedundancyQuestionMode;
  followup_suppression_enabled?: boolean;
  // Experimental: flattened onto the public survey payload so the respondent UI can route.
  survey_style?: SurveyStyle;
  discussion_guide?: DiscussionGuide | null;
}

// ---------------------------------------------------------------------------
// Experimental: conversational / discussion-guide survey style
// ---------------------------------------------------------------------------

export type SurveyStyle = 'traditional' | 'conversational';

// How the AI treats a whole question it judges already-answered (v3 breadth-first).
export type RedundancyQuestionMode = 'off' | 'carry_forward' | 'drop';

export interface MandatoryQuestion {
  id: string;
  text: string;
}

export interface DiscussionGuide {
  guide_markdown: string;
  mandatory_questions: MandatoryQuestion[];
  max_turns?: number;
}

/** One moderator turn returned by /moderator/next. */
export interface ModeratorNextResult {
  action: 'ask' | 'complete';
  turn_id?: string;
  question_text?: string | null;
  is_mandatory?: boolean;
  mandatory_question_id?: string | null;
  covered_topics?: string[];
}

export interface ModeratorAnswerPayload {
  turn_id: string;
  moderator_question: string;
  response_text: string;
  is_mandatory?: boolean;
  mandatory_question_id?: string | null;
  covered_topics?: string[];
  input_mode?: string;
  question_start_time?: string;
}

/** Conversational survey: get the moderator's next question (or completion). */
export async function getNextModeratorQuestion(sessionId: number): Promise<ModeratorNextResult> {
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/moderator/next`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

/** Conversational survey: persist one moderator question + respondent answer. */
export async function submitModeratorAnswer(
  sessionId: number,
  payload: ModeratorAnswerPayload
): Promise<{ status: string; session: SurveySession }> {
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/moderator/answer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

export interface SurveySession {
  id: number;
  state: string;
  current_question_index: number;
  survey_id: number;
  responses: any[];
  created_at: string;
  updated_at: string;
  completed_at?: string;
  responses_count: number;
}

/**
 * Parse questions for survey creation (no database records created)
 */
export async function parseQuestionsForSurvey(
  questions: string[],
  goal: string = '',
  context: string = ''
): Promise<{ task_id: string }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/parse`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      questions,
      goal,
      context,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create survey with approved questions
 */
export async function createSurveyWithQuestions(
  name: string,
  questions: ParsedQuestion[],
  title?: string,
  description?: string,
  instructions?: string,
  redirectConfig?: any,
  maxResponses?: number | null,
  blocks?: SurveyBlockDefinition[] | null
): Promise<{ survey_id: number; survey_data: Survey }> {
  const requestBody: any = {
    name,
    questions,
  };

  if (blocks !== undefined && blocks !== null) {
    requestBody.blocks = blocks;
  }
  if (title) requestBody.title = title;
  if (description) requestBody.description = description;
  if (instructions) requestBody.instructions = instructions;
  if (redirectConfig) requestBody.redirect_config = redirectConfig;
  if (maxResponses !== null && maxResponses !== undefined) {
    requestBody.max_responses = maxResponses;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get user's surveys
 */
export async function getUserSurveys(): Promise<{ surveys: Survey[] }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get specific survey details (authenticated)
 */
export async function getSurvey(surveyId: number): Promise<{ survey: Survey }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get survey details for public access (survey taking)
 */
export async function getPublicSurvey(surveyId: number): Promise<{ survey: Survey }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/public`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get specific survey by ID with full details (alias for getSurvey)
 */
export const getSurveyById = getSurvey;

/**
 * Update survey state (draft, active, paused, completed)
 */
export async function updateSurveyState(
  surveyId: number,
  newState: string
): Promise<{ survey: Survey }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/state`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      state: newState,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData?.detail;
    // Activation gate: going live with structural errors returns a 422 carrying the report. Surface
    // it as a SurveyValidationError so the UI can list what must be fixed (never overridable).
    if (response.status === 422 && detail && typeof detail === 'object' && detail.report) {
      throw new SurveyValidationError(detail.report, Boolean(detail.can_override), detail.error);
    }
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Session start result types
 */
export type SessionStartStatus =
  | 'success'
  | 'resumed'
  | 'quota_full'
  | 'already_completed'
  | 'screened_out'
  // Fraud-gate outcomes (returned before a session is created):
  | 'blocked'              // clear automation/headless browser
  | 'challenge_required'   // borderline — needs a Turnstile challenge
  | 'geo_blocked';         // non-US IP (soft gate, retryable)

export interface SessionStartResult {
  status?: SessionStartStatus;
  session_id?: number;
  survey_id?: number;
  session_data?: SurveySession;
  redirect_url?: string;
  message?: string;
  reason?: string;
  error?: string;
}

/**
 * Start or resume a survey session
 * If an external_tracking_id is provided and a session already exists,
 * the existing session is returned based on its state.
 *
 * clientSignals (automation-detection traits) and turnstileToken (a completed
 * CAPTCHA challenge) feed the backend fraud gate.
 */
export async function startSurveySession(
  surveyId: number,
  externalTrackingId?: string,
  clientSignals?: ClientSignals,
  turnstileToken?: string
): Promise<SessionStartResult> {
  const requestBody: any = {};

  if (externalTrackingId) {
    requestBody.external_tracking_id = externalTrackingId;
  }
  if (clientSignals) {
    requestBody.client_signals = clientSignals;
  }
  if (turnstileToken) {
    requestBody.turnstile_token = turnstileToken;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Result of submitting a response or follow-up. The backend may signal a skip (either
 * rule-based conditional skip or AI redundancy skip) and/or session termination.
 */
// Adaptive question scoping: when an upcoming open-text question's rubric was already partly
// answered earlier, the backend narrows its initial ask and returns the override here (keyed by
// the upcoming question_id). The runner shows `adapted_question` instead of the authored text.
export interface AdaptedQuestion {
  question_id: string;
  adapted_question: string;
  authored_question?: string;
  covered_points?: string[];
  inferred_quotes?: string[];
  remaining_points?: string[];
}

export interface SubmitResponseResult {
  session_data: SurveySession;
  status?: 'success' | 'terminated' | string;
  skip_applied?: boolean;
  next_question_index?: number;
  skip_reason?: 'conditionally_skipped' | 'ai_skipped' | string;
  termination_reason?: string;
  redirect_url?: string;
  adapted_question?: AdaptedQuestion;
}

/**
 * Get existing session by ID (public endpoint for session resume)
 * Returns null if session not found or not in resumable state
 */
export async function getSessionById(sessionId: number): Promise<SurveySession | null> {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      // Session not found or other error - return null to indicate not resumable
      return null;
    }

    const session = await response.json();

    // Only return session if it's in a resumable state
    if (session.state === 'in_progress') {
      return session;
    }

    return null;
  } catch {
    // Network error or other issue - return null
    return null;
  }
}

/**
 * Submit response to survey session
 */
export async function submitResponse(
  sessionId: number,
  questionId: string,
  responseData: any,
  questionType: string,
  questionStartTime?: string,
  advanceQuestion?: boolean,
  inputMode?: string
): Promise<SubmitResponseResult> {
  const requestBody: any = {
    question_id: questionId,
    response_data: responseData,
    question_type: questionType,
  };

  if (questionStartTime) {
    requestBody.question_start_time = questionStartTime;
  }

  if (advanceQuestion !== undefined) {
    requestBody.advance_question = advanceQuestion;
  }

  if (inputMode) {
    requestBody.input_mode = inputMode;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/responses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Complete survey session
 */
export async function completeSurveySession(sessionId: number): Promise<{
  session_data: SurveySession;
  is_complete: boolean;
  redirect_url?: string;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get survey session details
 */
export async function getSurveySession(sessionId: number): Promise<{ session: SurveySession }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Send heartbeat to keep session alive and track activity
 */
export function sendSessionHeartbeat(sessionId: number): void {
  const url = `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/heartbeat`;
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url);
  } else {
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  }
}

/**
 * Submit follow-up response to existing question
 */
export async function submitFollowupResponse(
  sessionId: number,
  questionId: string,
  followupQuestion: string,
  followupResponse: string
): Promise<SubmitResponseResult> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/followup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question_id: questionId,
      followup_question: followupQuestion,
      followup_response: followupResponse,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Start async analysis of response for potential follow-up question
 */
export async function startAnalyzeResponseForFollowup(
  sessionId: number,
  questionId: string,
  questionText: string,
  userResponse: string,
  questionType: string,
  context?: string,
  imageUrls?: string[]
): Promise<{
  status: 'processing';
  task_id: string;
  message: string;
}> {
  const requestBody: Record<string, unknown> = {
    question_id: questionId,
    question_text: questionText,
    user_response: userResponse,
    question_type: questionType,
    context: context,
  };

  // Include image URLs for photo_upload questions (for vision-based followup analysis)
  if (imageUrls && imageUrls.length > 0) {
    requestBody.image_urls = imageUrls;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/analyze-response`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Check status of analysis task
 */
export async function getAnalysisTaskStatus(
  sessionId: number,
  taskId: string
): Promise<{
  status: typeof ANALYSIS_TASK_STATES[keyof typeof ANALYSIS_TASK_STATES];
  analysis?: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  };
  error?: string;
  message: string;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/analysis-tasks/${taskId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Poll for analysis completion with timeout and progress callbacks
 */
export async function pollForAnalysisCompletion(
  sessionId: number,
  taskId: string,
  options: {
    timeout?: number; // milliseconds, default 60000 (60 seconds)
    pollInterval?: number; // milliseconds, default 1000 (1 second)
    onProgress?: (status: string, message: string) => void;
  } = {}
): Promise<{
  analysis: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  }
}> {
  const {
    timeout = ASYNC_ANALYSIS_CONFIG.POLL_TIMEOUT_MS,
    pollInterval = ASYNC_ANALYSIS_CONFIG.POLL_INTERVAL_MS,
    onProgress
  } = options;

  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    try {
      const result = await getAnalysisTaskStatus(sessionId, taskId);

      // Notify progress callback
      if (onProgress) {
        onProgress(result.status, result.message);
      }

      if (result.status === ANALYSIS_TASK_STATES.SUCCESS) {
        if (result.analysis) {
          return { analysis: result.analysis };
        } else {
          throw new Error('Analysis completed but no analysis data received');
        }
      } else if (result.status === ANALYSIS_TASK_STATES.FAILED) {
        throw new Error(result.error || 'Analysis task failed');
      }

      // Task is still pending or processing, wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));

    } catch (error) {
      // If it's a network error, retry after a short delay
      if (error instanceof Error && (error.message.includes('fetch') || error.message.includes('NetworkError'))) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }
      // Other errors should be thrown immediately
      throw error;
    }
  }

  throw new Error(`Analysis timeout after ${timeout / 1000} seconds`);
}

/**
 * Analyze response for potential follow-up question (maintains backward compatibility)
 * Now uses async processing with polling
 */
export async function analyzeResponseForFollowup(
  sessionId: number,
  questionId: string,
  questionText: string,
  userResponse: string,
  questionType: string,
  options?: {
    timeout?: number;
    pollInterval?: number;
    context?: string;
    imageUrls?: string[];
    onProgress?: (status: string, message: string) => void;
  }
): Promise<{
  analysis: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  }
}> {
  // Start the analysis
  const taskResponse = await startAnalyzeResponseForFollowup(
    sessionId,
    questionId,
    questionText,
    userResponse,
    questionType,
    options?.context,
    options?.imageUrls
  );

  // Poll for completion
  return pollForAnalysisCompletion(sessionId, taskResponse.task_id, options);
}

/**
 * Get all responses for a survey - simple list view
 */
export async function getSurveyResponses(surveyId: number): Promise<{
  survey_id: number;
  total_responses: number;
  total_attempts?: number;
  responses: Array<{
    session_id: number;
    state?: string;
    response_number?: number | null;
    completed_at: string | null;
    started_at?: string | null;
    last_activity_at?: string | null;
    responses_count: number;
    external_tracking_id?: string | null;
    customer_id?: string | number | null;
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/responses`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

interface ResponseItem {
  type: 'single_choice' | 'multiple_choice' | 'yes_no' | 'rating_scale' | 'likert_scale' | 'short_text' | 'long_text' | 'dropdown' | 'nps' | 'numeric_input' | 'ranking' | 'matrix_rating' | 'max_diff' | 'conjoint_analysis' | 'original_response' | 'followup_question' | 'followup_response';
  value: any;
  timestamp: string;
}

/**
 * Get detailed responses for a specific session
 */
export async function getSessionResponses(sessionId: number): Promise<{
  session_id: number;
  completed_at: string;
  responses: Array<{
    question_id?: string;
    question_text: string;
    question_type: string;
    response_data: ResponseItem[];
    created_at: string;
    state?: string;  // includes 'ai_skipped'
    ai_inference?: {
      inferred_answer?: any;
      covered_by?: string[];
      citation?: string;
      confidence?: number;
      reason?: string;
    };
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/responses`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get aggregated MaxDiff summary for a survey across all sessions
 */
export async function getSurveyMaxDiffSummary(surveyId: number): Promise<{
  survey_id: number;
  maxdiff_questions: Array<{
    question_id: string;
    question_text: string;
    total_sessions: number;
    total_sets: number;
    claims: Array<{
      claim: string;
      most_count: number;
      least_count: number;
      net_score: number;
      most_percentage: number;
      least_percentage: number;
      rank: number;
    }>;
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/maxdiff-summary`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get aggregated Image Hotspot summary for a survey across all sessions
 */
export async function getSurveyImageHotspotSummary(surveyId: number): Promise<{
  survey_id: number;
  image_hotspot_questions: Array<{
    question_id: string;
    question_text: string;
    image_url: string;
    bounding_boxes: Array<{
      id: string;
      label: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
    ranking_count: number;
    total_sessions: number;
    total_clicks: number;
    aggregated_clicks: Array<{
      x: number;
      y: number;
      rank: number;
      product_id: string | null;
      product_label: string | null;
      timestamp_ms: number;
    }>;
    response_distribution: Array<{
      option: string;
      count: number;
      percentage: number;
      ranking_breakdown: Record<string, number>;
    }>;
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/image-hotspot-summary`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update survey questions, quota, and redirect config
 */
export async function updateSurveyQuestions(
  surveyId: number,
  questions: any[],
  redirectConfig?: any,
  maxResponses?: number | null,
  blocks?: SurveyBlockDefinition[] | null,
  minLinkedInConnections?: number | null,
  enableAiRedundancySkip?: boolean,
  surveyStyle?: SurveyStyle,
  discussionGuide?: DiscussionGuide | null,
  redundancyQuestionMode?: RedundancyQuestionMode,
  followupSuppressionEnabled?: boolean,
  acknowledgeWarnings?: boolean,
  adaptiveProbing?: boolean,
  crossQuestionCoverage?: boolean,
  adaptiveQuestionScoping?: boolean,
  surveyLevelProbingEnabled?: boolean
): Promise<{
  survey: any;
  report?: import('./surveyValidation').ValidationReport;
}> {
  const body: any = { questions };

  if (blocks !== undefined) {
    body.blocks = blocks;
  }
  if (redirectConfig !== undefined) {
    body.redirect_config = redirectConfig;
  }

  if (maxResponses !== undefined) {
    body.max_responses = maxResponses;
  }

  if (enableAiRedundancySkip !== undefined) {
    body.enable_ai_redundancy_skip = enableAiRedundancySkip;
  }

  if (redundancyQuestionMode !== undefined) {
    body.redundancy_question_mode = redundancyQuestionMode;
  }

  if (followupSuppressionEnabled !== undefined) {
    body.followup_suppression_enabled = followupSuppressionEnabled;
  }

  if (adaptiveProbing !== undefined) {
    body.adaptive_probing = adaptiveProbing;
  }

  if (crossQuestionCoverage !== undefined) {
    body.cross_question_coverage = crossQuestionCoverage;
  }

  if (adaptiveQuestionScoping !== undefined) {
    body.adaptive_question_scoping = adaptiveQuestionScoping;
  }

  if (surveyLevelProbingEnabled !== undefined) {
    body.survey_level_probing_enabled = surveyLevelProbingEnabled;
  }

  // Experimental conversational style (stored in ai_features server-side).
  if (surveyStyle !== undefined) {
    body.survey_style = surveyStyle;
  }
  if (discussionGuide !== undefined && discussionGuide !== null) {
    body.discussion_guide = discussionGuide;
  }
  if (acknowledgeWarnings !== undefined) {
    body.acknowledge_warnings = acknowledgeWarnings;
  }

  if (minLinkedInConnections !== undefined) {
    body.min_linkedin_connections = minLinkedInConnections;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/questions`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // A 422 from the referential-integrity gate carries a structured report under `detail`.
    const detail = errorData?.detail;
    if (response.status === 422 && detail && typeof detail === 'object' && detail.report) {
      throw new SurveyValidationError(detail.report, Boolean(detail.can_override), detail.error);
    }
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Authoring-time redundancy audit (ADVISORY — never writes skip flags itself;
// the author must explicitly accept "Auto-skip duplicates" per cluster)
// ---------------------------------------------------------------------------

/** Mirror of the backend's NEVER_SKIP_QUESTION_TYPES (interview_constants.py): question
 * types that can never be skipped/auto-answered — a number/grid/ranking/diary cannot be
 * reconstructed from prose. The UI must never flag these for skipping. */
export const NEVER_SKIP_QUESTION_TYPES = new Set([
  'rating_scale', 'likert_scale', 'nps', 'numeric_input', 'matrix_rating',
  'ranking', 'max_diff', 'conjoint_analysis', 'percentage_allocation',
  'image_hotspot', 'photo_upload', 'info_screen',
]);

export interface RedundancyCluster {
  question_ids: string[];
  theme: string;
  severity: 'duplicate' | 'strong' | 'mild' | string;
  suggestion: string;
}

/** Run the redundancy audit over a survey's question list (no responses needed).
 * Also generates + persists per-question info_targets server-side and returns the updated
 * questions array — callers MUST use that array as their mapping base. */
export async function auditSurveyRedundancy(surveyId: number): Promise<{
  survey_id: number;
  question_count: number;
  clusters: RedundancyCluster[];
  questions?: any[];
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/redundancy-audit`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Survey Groups (between-subject distribution layer)
// ---------------------------------------------------------------------------

export interface SurveyGroup {
  id: number;
  name: string;
  group_count: number;
  group_labels: string[];
  survey_ids: number[];
  questions: any[];
  blocks?: SurveyBlockDefinition[] | null;
  allocation_ratios?: number[] | null;
  min_linkedin_connections?: number | null;
  user_id: number;
  created_at: string;
  updated_at: string;
  // Adaptive config surfaced from the child surveys (kept in sync on save).
  ai_features?: { adaptive_probing?: boolean; survey_level_probing_enabled?: boolean; [k: string]: unknown } | null;
  // Per-survey settings surfaced from the child surveys so the group editor loads their real
  // state. Without these the redirect form loads blank and the next save nulls the children's
  // panel redirect — see the round-trip fix in SurveyGroupDetails.
  redirect_config?: {
    redirect_base_url: string;
    screened_out_url?: string | null;
    tracking_param: { [key: string]: string };
    completion_params: { [key: string]: string } | null;
    screen_out_params: { [key: string]: string } | null;
    quota_full_params: { [key: string]: string } | null;
  } | null;
  max_responses?: number | null;
}

export interface SurveyGroupCreatePayload {
  name: string;
  group_count: number;
  questions: any[];
  blocks?: SurveyBlockDefinition[] | null;
  title?: string;
  description?: string;
  instructions?: string;
  redirect_config?: any;
  max_responses_per_group?: number | null;
  allocation_ratios?: number[] | null;
  min_linkedin_connections?: number | null;
}

export async function createSurveyGroup(
  payload: SurveyGroupCreatePayload
): Promise<{ group_id: number; group: SurveyGroup; distribution_url: string }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function getSurveyGroups(): Promise<{ groups: SurveyGroup[] }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function getSurveyGroupDetail(groupId: number): Promise<{
  group: SurveyGroup;
  session_counts: Record<number, { total: number; completed: number }>;
  welcome_data?: { title?: string; description?: string; instructions?: string };
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export function getSurveyGroupExportUrl(groupId: number): string {
  return `${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/export/xlsx`;
}

export async function getSurveyGroupResponses(groupId: number): Promise<{
  group_id: number;
  total_responses: number;
  responses: any[];
  per_group: Record<number, { label: string; responses: any[] }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/responses`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function updateSurveyGroupQuestions(
  groupId: number,
  questions: any[],
  redirectConfig?: any,
  maxResponses?: number | null,
  allocationRatios?: number[] | null,
  blocks?: SurveyBlockDefinition[] | null,
  acknowledgeWarnings?: boolean,
  adaptiveProbing?: boolean,
  crossQuestionCoverage?: boolean,
  adaptiveQuestionScoping?: boolean,
  enableAiRedundancySkip?: boolean,
  welcomeData?: { title?: string; description?: string; instructions?: string },
  surveyLevelProbingEnabled?: boolean,
): Promise<{ group: SurveyGroup; report?: import('./surveyValidation').ValidationReport }> {
  const body: any = { questions };
  if (blocks !== undefined) body.blocks = blocks;
  if (redirectConfig !== undefined) body.redirect_config = redirectConfig;
  if (maxResponses !== undefined) body.max_responses = maxResponses;
  if (allocationRatios !== undefined) body.allocation_ratios = allocationRatios;
  if (acknowledgeWarnings !== undefined) body.acknowledge_warnings = acknowledgeWarnings;
  if (adaptiveProbing !== undefined) body.adaptive_probing = adaptiveProbing;
  if (crossQuestionCoverage !== undefined) body.cross_question_coverage = crossQuestionCoverage;
  if (adaptiveQuestionScoping !== undefined) body.adaptive_question_scoping = adaptiveQuestionScoping;
  if (enableAiRedundancySkip !== undefined) body.enable_ai_redundancy_skip = enableAiRedundancySkip;
  if (surveyLevelProbingEnabled !== undefined) body.survey_level_probing_enabled = surveyLevelProbingEnabled;
  // Welcome screen fields (propagated to all child surveys)
  if (welcomeData?.title !== undefined) body.title = welcomeData.title;
  if (welcomeData?.description !== undefined) body.description = welcomeData.description;
  if (welcomeData?.instructions !== undefined) body.instructions = welcomeData.instructions;

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/questions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = err?.detail;
    if (response.status === 422 && detail && typeof detail === 'object' && detail.report) {
      throw new SurveyValidationError(detail.report, Boolean(detail.can_override), detail.error);
    }
    throw new Error((detail && typeof detail === 'object' ? detail.error : detail) || `HTTP ${response.status}`);
  }
  return response.json();
}

/** Dry-run structural validation for a survey group's master questions (no write). */
export async function validateSurveyGroup(
  groupId: number,
  questions: unknown[],
  blocks?: unknown[] | null,
  opts?: { applyAutoFixes?: boolean; fixes?: Record<string, unknown>[] }
): Promise<{ report: import('./surveyValidation').ValidationReport; fixed_questions?: unknown[] }> {
  const body: Record<string, unknown> = { questions };
  if (blocks !== undefined) body.blocks = blocks;
  if (opts?.applyAutoFixes) body.apply_auto_fixes = true;
  if (opts?.fixes && opts.fixes.length) body.fixes = opts.fixes;

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = err?.detail;
    throw new Error((detail && typeof detail === 'object' ? detail.error : detail) || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return { report: data.report ?? { errors: [], warnings: [] }, fixed_questions: data.fixed_questions };
}

/**
 * Update survey welcome screen data (title, description, instructions)
 */
export async function updateSurveyWelcomeData(
  surveyId: number,
  welcomeData: {
    title?: string;
    description?: string;
    instructions?: string;
  }
): Promise<{
  survey: Survey;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/welcome`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(welcomeData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// =============================================================================
// SURVEY ANALYSIS API
// =============================================================================

export interface SurveyAnalysisStatus {
  analysis_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  progress_message?: string;
  error_message?: string;
}

export interface DistributionItem {
  count: number;
  percentage: number;
  // Different question types use different field names for the label
  choice?: string;   // SingleChoice, Dropdown
  rating?: number;   // RatingScale
  level?: string;    // LikertScale
  score?: number;    // NPS
  value?: number;    // Numeric
}

export interface RankingItem {
  item?: string;        // Ranking
  option?: string;      // MaxDiff
  average_rank?: number;
  first_place_count?: number;
  last_place_count?: number;
  most_count?: number;  // MaxDiff
  least_count?: number; // MaxDiff
  net_score?: number;   // MaxDiff
}

export interface MatrixItem {
  item: string;
  average: number;
  count: number;
  distribution?: Record<number, number>;
}

export interface PreferenceItem {
  choice: string;
  count: number;
  percentage: number;
}

export interface ProductItem {
  product: string;
  total_clicks: number;
  first_choice_count: number;
  rank_distribution?: Record<number, number>;
}

export interface QuestionAnalysis {
  question_id: string;
  question_text: string;
  type: string;
  total_responses: number;
  tldr?: string;
  synthesis?: string;
  counter_arguments?: string[];
  distribution?: DistributionItem[];
  statistics?: Record<string, number>;
  responses?: (string | number | Record<string, unknown>)[];
  followup_responses?: string[];
  // Additional fields for specific question types
  rankings?: RankingItem[];      // Ranking, MaxDiff
  items?: MatrixItem[];          // Matrix
  preferences?: PreferenceItem[]; // Conjoint
  products?: ProductItem[];       // Image Hotspot
  // Yes/No fields
  yes_count?: number;
  no_count?: number;
  yes_percentage?: number;
  no_percentage?: number;
  // Percentage Allocation fields
  allocation_target?: number;
}

export interface SurveySynthesis {
  markdown: string;
}

export interface SurveyAnalysisResult {
  analysis_id: number;
  survey_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  analysis_goal?: string;
  responses_analyzed: number;
  question_analyses?: Record<string, QuestionAnalysis>;
  survey_synthesis?: SurveySynthesis;
  created_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface AnalysisHistoryItem {
  analysis_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  analysis_goal?: string;
  responses_analyzed: number;
  created_at?: string;
  completed_at?: string;
}

/**
 * Trigger a new AI analysis for a survey
 */
export async function triggerSurveyAnalysis(
  surveyId: number,
  analysisGoal?: string
): Promise<{
  analysis_id: number;
  task_id: string;
  status: string;
  message: string;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      analysis_goal: analysisGoal,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get the latest analysis for a survey
 */
export async function getSurveyAnalysis(surveyId: number): Promise<SurveyAnalysisResult> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a specific analysis by ID
 */
export async function getSurveyAnalysisById(
  surveyId: number,
  analysisId: number
): Promise<SurveyAnalysisResult> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/${analysisId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get analysis status and progress
 */
export async function getSurveyAnalysisStatus(
  surveyId: number,
  analysisId: number
): Promise<SurveyAnalysisStatus> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/${analysisId}/status`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get analysis history for a survey
 */
export async function getSurveyAnalysisHistory(surveyId: number): Promise<{
  analyses: AnalysisHistoryItem[];
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/history`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Poll for analysis completion
 */
export async function pollSurveyAnalysisCompletion(
  surveyId: number,
  analysisId: number,
  options: {
    timeout?: number;
    pollInterval?: number;
    onProgress?: (progress: number, message: string) => void;
  } = {}
): Promise<SurveyAnalysisResult> {
  const { timeout = 600000, pollInterval = 3000, onProgress } = options;
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const status = await getSurveyAnalysisStatus(surveyId, analysisId);

    if (onProgress) {
      onProgress(status.progress, status.progress_message || '');
    }

    if (status.status === 'completed') {
      return getSurveyAnalysisById(surveyId, analysisId);
    }

    if (status.status === 'failed') {
      throw new Error(status.error_message || 'Analysis failed');
    }

    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }

  throw new Error(`Analysis timeout after ${timeout / 1000} seconds`);
}

// =============================================================================
// LINKEDIN VERIFICATION API
// =============================================================================

export interface LinkedInVerification {
  id: number;
  survey_id: number;
  session_id: number | null;
  linkedin_profile_url: string;
  linkedin_user_id: string | null;
  full_name: string | null;
  headline: string | null;
  location: string | null;
  connections_count: number;
  profile_picture_url: string | null;
  company: string | null;
  job_title: string | null;
  is_eligible: boolean;
  verification_status: string;
  rejection_reason: string | null;
  created_at: string;
  verified_at: string | null;
}

export interface LinkedInVerificationListResponse {
  verifications: LinkedInVerification[];
  total: number;
}

/**
 * Get LinkedIn verifications for a survey
 */
export async function getLinkedInVerifications(
  surveyId: number,
  limit: number = 100,
  offset: number = 0
): Promise<LinkedInVerificationListResponse> {
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/linkedin/surveys/${surveyId}/verifications?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Generate LinkedIn share URL for a survey
 * This URL will initiate the LinkedIn verification flow
 */
export function getLinkedInShareUrl(shareId: string): string {
  return `${window.location.origin}/surveys/${shareId}/linkedin`;
}

/**
 * Check if a survey has LinkedIn verification enabled
 */
export function hasLinkedInVerification(survey: Survey | null): boolean {
  if (!survey) return false;
  const minConnections = (survey as any).min_linkedin_connections;
  return minConnections != null && minConnections > 0;
}
